import { useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { getFormWinrate } from 'app/components/Blocks/FormGuideBlock/utils';
import TeamAvatar from 'app/components/TeamAvatar';
import { partitionArray, sort } from 'app/utils/array';
import BlockGradTemplate from 'app/components/DesignVersion/Grad/Blocks/BlockGradTemplate';
import TimelineArrowIcon from 'app/icons/arrow-min-right.svg';
import ArrowIcon from 'app/icons/arrow-min.svg';

import style from './style.module.scss';

const NUMBER_OF_ENCOUNTERS_DISPLAYED = 5;

interface Props {
  opponents: OpponentWithForm[] | undefined;
}

const FormGuideBlockGrad: FC<Props> = ({ opponents }) => {
  const { t } = useTranslation();

  const [expanded, setExpanded] = useState(true);

  const opponent1 = opponents?.at(0);
  const opponent2 = opponents?.at(1);

  const opponent1Form = opponent1?.form.slice(0, NUMBER_OF_ENCOUNTERS_DISPLAYED);
  const opponent2Form = opponent2?.form.slice(0, NUMBER_OF_ENCOUNTERS_DISPLAYED);

  const opponent1HasForm = Boolean(opponent1Form && opponent1Form.length);
  const opponent2HasForm = Boolean(opponent2Form && opponent2Form.length);

  const opponentsData = [
    opponent1Form ? sort(opponent1Form, encounter => encounter.begin_at) : undefined,
    opponent2Form ? sort(opponent2Form, encounter => encounter.begin_at) : undefined
  ];

  return (
    <BlockGradTemplate
      opponentsLogo={[opponent1, opponent2]}
      hasData={opponent1HasForm || opponent2HasForm}
      contentClassName={style.formGuideContent}
      title={t('block-grad-form_guide-title', {
        nbOfMatchesDisplayed: NUMBER_OF_ENCOUNTERS_DISPLAYED
      })}
    >
      {opponentsData.map((opponentForm, opponentIndex) => {
        if (opponentForm && opponentForm.length > 0) {
          const opponent = opponents?.at(opponentIndex);
          const opponentWinrate = getFormWinrate(opponent?.id || -1, opponentForm);

          return (
            <div
              key={`opponent_${opponentIndex}`}
              className={classNames(
                style.formGuideOpponent,
                style[`formGuideOpponent${opponentIndex === 0 ? '1' : '2'}`]
              )}
            >
              <div className={style.formGuideTimeline}>
                {new Array(NUMBER_OF_ENCOUNTERS_DISPLAYED).fill(null).map((_, index) => {
                  const encounter = opponentForm.at(index);
                  if (!encounter)
                    return (
                      <div
                        key={`opponent${opponentIndex}_form_timeline_${index}`}
                        className={style.formGuideEncounterResultPlaceholder}
                      />
                    );

                  const isWin = encounter.winner_id === opponent?.id;
                  return (
                    <div
                      key={`opponent${opponentIndex}_form_timeline_${index}`}
                      className={classNames(style.formGuideEncounterResult, {
                        [style.formGuideEncounterWin]: isWin
                      })}
                    >
                      <span>{isWin ? 'W' : 'L'}</span>
                    </div>
                  );
                })}
                <TimelineArrowIcon className={style.formGuideTimelineArrow} />
              </div>
              {expanded && (
                <ul>
                  {[...opponentForm].reverse().map((encounter, index) => {
                    const isWin = encounter.winner_id === opponent?.id;

                    const [[thisOpponent], [otherOpponent]] = partitionArray(
                      encounter.opponents,
                      encounterOpponent => (encounterOpponent.id === opponent?.id ? 0 : 1)
                    );

                    return (
                      <li
                        key={`opponent${opponentIndex}_form_${index}`}
                        className={style.formGuideEncounter}
                      >
                        <div
                          className={classNames(style.formGuideEncounterResult, {
                            [style.formGuideEncounterWin]: isWin
                          })}
                        >
                          <span>{isWin ? 'W' : 'L'}</span>
                        </div>
                        <div className={style.formGuideEncounterTeams}>
                          <TeamAvatar
                            team={thisOpponent}
                            className={style.formGuideEncounterTeam}
                          />
                          <div className={style.formGuideEncounterScore}>
                            <span>{thisOpponent?.score}</span>
                            <span> - </span>
                            <span>{otherOpponent?.score}</span>
                          </div>
                          <TeamAvatar
                            team={otherOpponent}
                            className={style.formGuideEncounterTeam}
                          />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
              <div className={style.formGuideOpponentWinrate}>
                {t('block-grad-form_guide-winrate', { winrate: opponentWinrate })}
              </div>
            </div>
          );
        }
        return (
          <div key={`opponent_${opponentIndex}`} className={style.noStat}>
            {t('error_no-data_compact')}
          </div>
        );
      })}
      <button className={style.expandButton} onClick={() => setExpanded(!expanded)} type="button">
        <ArrowIcon
          className={classNames(style.expandButtonArrow, {
            [style.expandButtonArrowOpened]: expanded
          })}
        />
      </button>
    </BlockGradTemplate>
  );
};

FormGuideBlockGrad.displayName = 'FormGuideBlockGrad';

export default FormGuideBlockGrad;
